import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Download, FileText, Table2, Code, AlertTriangle, Link, Link2, Sheet, Zap, MapPin, Gauge, Hash, List, Settings, MoreHorizontal } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { FieldEditor } from './FieldEditor';
import { UserDefinedFields } from './UserDefinedFields';
import { APIIntegrationModal } from './APIIntegrationModal';
import { GoogleSheetsIntegrationModal } from './GoogleSheetsIntegrationModal';
import { MeterTable } from './MeterTable';
import { LineItemsTable } from './LineItemsTable';
import { IdentifierTable } from './IdentifierTable';
import { InlineFieldEditor } from './InlineFieldEditor';
import { UserDefinedFieldsPanel } from './UserDefinedFieldsPanel';
import { CAT } from '@/shared/CAT';
import { canonicalTransform, type CanonicalResult } from '@/utils/canonicalTransform';
import { exportOneCSV } from '@/utils/exportOneCSV';
import { unwrapApiPayload } from '@/utils/unwrapApiPayload';
import { getSelectedPdfText } from '@/utils/pdfSelection';
import type { BillHeader, LineItem } from '@/utils/parserTransform';
import { useState, useMemo, useCallback } from 'react';
import { ExportCSVCard, ConnectAPICard, ConnectGoogleSheetsCard } from './ActionCard';
import { APIConnectorModal } from './APIConnectorModal';
import { GoogleSheetsConnectorModal } from './GoogleSheetsConnectorModal';
import { Separator } from '@/components/ui/separator';
import { sendToExternalApi } from '@/integrations/apiConnector';
import { sendToGoogleSheets } from '@/integrations/googleSheetsWebhook';
import { getNormalizedPayloadForExport } from '@/utils/getNormalizedPayload';
import { toast } from 'sonner';
import './parser-sandbox.css';

interface CanonicalExtractedDataDisplayProps {
  header: BillHeader;
  lineItems: LineItem[];
  rawData: any;
  onExportCSV: () => void;
  onFieldUpdate: (field: string, value: string) => void;
  onAddUserDefinedField?: (field: any) => void;
  onUpdateUserDefinedField?: (id: string, field: any) => void;
  onDeleteUserDefinedField?: (id: string) => void;
}

export function CanonicalExtractedDataDisplay({ 
  header, 
  lineItems, 
  rawData, 
  onExportCSV,
  onFieldUpdate,
  onAddUserDefinedField,
  onUpdateUserDefinedField,
  onDeleteUserDefinedField
}: CanonicalExtractedDataDisplayProps) {
  // Alias for consistency with the rest of the code
  const onUpdateField = onFieldUpdate;
  
  // User-Defined Fields state
  const [userDefinedFields, setUserDefinedFields] = useState<Array<{id: string; name: string; value: string}>>([]);
  
  // Edited fields state for tracking changes
  const [editedFields, setEditedFields] = useState<Record<string, any>>({});
  
  // Loading states for integrations
  const [loadingApi, setLoadingApi] = useState(false);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [loadingCSV, setLoadingCSV] = useState(false);
  
  // Integration status tracking
  const [apiConnected, setApiConnected] = useState(false);
  const [sheetsConnected, setSheetsConnected] = useState(false);
  
  // Force 98% confidence display
  const confidenceDisplay = 98;
  
  // Unwrap API payload and transform to canonical format
  const canonicalResult: CanonicalResult = useMemo(() => {
    console.log('🔍 [CanonicalExtractedDataDisplay] Raw data:', rawData);
    const result = canonicalTransform(rawData);
    console.log('🔍 [CanonicalExtractedDataDisplay] Transform result:', result);
    console.log('🔍 [CanonicalExtractedDataDisplay] Categories:', Object.keys(result.categories));
    console.log('🔍 [CanonicalExtractedDataDisplay] Basic fields:', result.categories[CAT.Basic]?.length || 0);
    console.log('🔍 [CanonicalExtractedDataDisplay] Financial fields:', result.categories[CAT.Financial]?.length || 0);
    console.log('🔍 [CanonicalExtractedDataDisplay] Other fields:', result.categories[CAT.Other]?.length || 0);
    console.log('🔍 [CanonicalExtractedDataDisplay] Meter fields:', result.categories[CAT.Meter]?.length || 0);
    console.log('🔍 [CanonicalExtractedDataDisplay] Identifier fields:', result.categories[CAT.Identifier]?.length || 0);
    console.log('🔍 [CanonicalExtractedDataDisplay] LineItems fields:', result.categories[CAT.LineItems]?.length || 0);
    
    // Debug the new arrays
    console.debug('[View] counts', {
      basic: result.categories[CAT.Basic]?.length || 0,
      identifierRows: result.identifiers.length,
      meterRows: result.meters.length,
      lineItemRows: result.lineItems.length,
    });
    
    return result;
  }, [rawData]);
  
  // Use the same CAT keys in the UI
  const count = useCallback((k: keyof typeof CAT) => canonicalResult.categories[CAT[k]]?.length ?? 0, [canonicalResult]);
  
  const fieldCounts = useMemo(() => {
    return {
      [CAT.Basic]: count('Basic'),
      [CAT.Financial]: count('Financial'),
      [CAT.Address]: count('Address'),
      [CAT.Usage]: count('Usage'),
      [CAT.Meter]: count('Meter'),
      [CAT.Identifier]: count('Identifier'),
      [CAT.LineItems]: count('LineItems'),
      [CAT.Technical]: count('Technical'),
      [CAT.Other]: count('Other'),
    };
  }, [count]);
  

  // User-Defined Fields handlers
  const handleUDFChange = useCallback((fields: Array<{id: string; name: string; value: string}>) => {
    setUserDefinedFields(fields);
  }, []);

  const handleUDFSave = useCallback((fields: Array<{id: string; name: string; value: string}>) => {
    // TODO: Implement backend save if needed
    console.log('Saving UDF fields:', fields);
  }, []);

  // Field editing handlers
  const handleFieldEdit = useCallback((keyPath: string, newValue: any) => {
    setEditedFields(prev => ({
      ...prev,
      [keyPath]: newValue
    }));
  }, []);

  const handleFieldRevert = useCallback((keyPath: string) => {
    setEditedFields(prev => {
      const newFields = { ...prev };
      delete newFields[keyPath];
      return newFields;
    });
  }, []);

  // CSV Export handler
  const handleExportCSV = useCallback(async () => {
    setLoadingCSV(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate processing
      exportOneCSV(canonicalResult);
      toast.success('CSV exported successfully');
    } catch (error: any) {
      toast.error('CSV export failed');
    } finally {
      setLoadingCSV(false);
    }
  }, [canonicalResult]);

  // API Integration handler
  const handleConnectAPI = useCallback(async (config: any) => {
    setLoadingApi(true);
    try {
      const payload = getNormalizedPayloadForExport(canonicalResult, rawData, userDefinedFields);
      await sendToExternalApi(config, payload);
      setApiConnected(true);
      toast.success('Data sent to external API successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send to API');
      throw error;
    } finally {
      setLoadingApi(false);
    }
  }, [canonicalResult, rawData, userDefinedFields]);

  // Google Sheets Integration handler
  const handleConnectSheets = useCallback(async (config: any) => {
    setLoadingSheets(true);
    try {
      const payload = getNormalizedPayloadForExport(canonicalResult, rawData, userDefinedFields);
      const result = await sendToGoogleSheets(config, payload);
      
      if (result.success) {
        setSheetsConnected(true);
        
        if (result.csvDownloaded) {
          toast.success(`CSV downloaded! ${result.rowsAdded} rows ready for Google Sheets import.`, {
            duration: 10000,
            description: 'Check your downloads folder and follow the instructions to import into Google Sheets.'
          });
        } else {
          toast.success('Data sent to Google Sheets successfully');
        }
      } else {
        throw new Error(result.message);
      }
      
      return result;
    } catch (error: any) {
      toast.error(error.message || 'Failed to send to Google Sheets');
      throw error;
    } finally {
      setLoadingSheets(false);
    }
  }, [canonicalResult, rawData, userDefinedFields]);

  // Transform functions for table data
  const transformMeterData = useCallback((meterFields: any[]) => {
    // Group meter fields by meter number and create table rows
    const meterMap = new Map<string, any>();
    
    meterFields.forEach(field => {
      const key = field.keyPath.split('.')[0]; // Get identifier index
      if (!meterMap.has(key)) {
        meterMap.set(key, {
          identifier: '',
          meterNumber: '',
          readType: '',
          periodFrom: '',
          periodTo: '',
          previousReading: '',
          currentReading: '',
          quantity: '',
          quantityUOM: '',
          multiplier: ''
        });
      }
      
      const meter = meterMap.get(key);
      const fieldName = field.label.toLowerCase().replace(/\s+/g, '');
      
      switch (fieldName) {
        case 'identifier':
          meter.identifier = field.value;
          break;
        case 'meternumber':
          meter.meterNumber = field.value;
          break;
        case 'readtype':
          meter.readType = field.value;
          break;
        case 'periodfrom':
          meter.periodFrom = field.value;
          break;
        case 'periodto':
          meter.periodTo = field.value;
          break;
        case 'previousreading':
          meter.previousReading = field.value;
          break;
        case 'currentreading':
          meter.currentReading = field.value;
          break;
        case 'quantity':
          meter.quantity = field.value;
          break;
        case 'quantityuom':
          meter.quantityUOM = field.value;
          break;
        case 'multiplier':
          meter.multiplier = field.value;
          break;
      }
    });
    
    return Array.from(meterMap.values());
  }, []);

  const transformIdentifierData = useCallback((identifierFields: any[]) => {
    // Group identifier fields by identifier and create table rows
    const identifierMap = new Map<string, any>();
    
    identifierFields.forEach(field => {
      const key = field.keyPath.split('.')[0]; // Get identifier index
      if (!identifierMap.has(key)) {
        identifierMap.set(key, {
          identifier: '',
          utilityType: '',
          subAccount: '',
          serviceTotal: '',
          serviceAddress: '',
          serviceCity: '',
          serviceState: '',
          serviceCountry: '',
          servicePostCode: ''
        });
      }
      
      const identifier = identifierMap.get(key);
      const fieldName = field.label.toLowerCase().replace(/\s+/g, '');
      
      switch (fieldName) {
        case 'identifier':
          identifier.identifier = field.value;
          break;
        case 'utilitytype':
          identifier.utilityType = field.value;
          break;
        case 'subaccount':
          identifier.subAccount = field.value;
          break;
        case 'servicetotal':
          identifier.serviceTotal = field.value;
          break;
        case 'serviceaddress':
          identifier.serviceAddress = field.value;
          break;
        case 'servicecity':
          identifier.serviceCity = field.value;
          break;
        case 'servicestate':
          identifier.serviceState = field.value;
          break;
        case 'servicecountry':
          identifier.serviceCountry = field.value;
          break;
        case 'servicepostcode':
          identifier.servicePostCode = field.value;
          break;
      }
    });
    
    return Array.from(identifierMap.values());
  }, []);

  const transformLineItemData = useCallback((lineItemFields: any[]) => {
    // Group line item fields and create table rows
    const itemMap = new Map<string, any>();
    
    lineItemFields.forEach(field => {
      const key = field.keyPath.split('.')[0]; // Get identifier index
      if (!itemMap.has(key)) {
        itemMap.set(key, {
          identifier: '',
          meterNumber: '',
          lineItemDescription: '',
          quantity: '',
          quantityUOM: '',
          rate: '',
          rateUOM: '',
          total: ''
        });
      }
      
      const item = itemMap.get(key);
      const fieldName = field.label.toLowerCase().replace(/\s+/g, '');
      
      switch (fieldName) {
        case 'identifier':
          item.identifier = field.value;
          break;
        case 'meternumber':
          item.meterNumber = field.value;
          break;
        case 'lineitemdescription':
          item.lineItemDescription = field.value;
          break;
        case 'quantity':
          item.quantity = field.value;
          break;
        case 'quantityuom':
          item.quantityUOM = field.value;
          break;
        case 'rate':
          item.rate = field.value;
          break;
        case 'rateuom':
          item.rateUOM = field.value;
          break;
        case 'total':
          item.total = field.value;
          break;
      }
    });
    
    return Array.from(itemMap.values());
  }, []);

  // Helper function to format field values with credit detection
  const formatFieldValue = (value: any): string => {
    if (typeof value === 'number') {
      if (value < 0) {
        return `−$${Math.abs(value).toFixed(2)}`;
      }
      if (value % 1 !== 0) {
        return value.toFixed(2);
      }
      return value.toString();
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '';
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // Helper function to check if a value is a credit (negative amount)
  const isCredit = (value: any): boolean => {
    return typeof value === 'number' && value < 0;
  };

  // Helper function to get confidence badge
  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.85) return { icon: '🟢', text: 'High', className: 'bg-green-100 text-green-800' };
    if (confidence >= 0.5) return { icon: '🟡', text: 'Medium', className: 'bg-yellow-100 text-yellow-800' };
    return { icon: '🔴', text: 'Low', className: 'bg-red-100 text-red-800' };
  };

  // Render field with confidence and review status
  const renderField = (field: any, category: string) => {
    const confidence = field.confidence || 1.0;
    const needsReview = field.needsReview || false;
    const confidenceBadge = getConfidenceBadge(confidence);
    const isEdited = editedFields.hasOwnProperty(field.keyPath);
    
    // Determine field type for inline editing
    const getFieldType = (label: string, value: any): 'money' | 'date' | 'number' | 'text' | 'uom' => {
      const lowerLabel = label.toLowerCase();
      if (lowerLabel.includes('amount') || lowerLabel.includes('total') || lowerLabel.includes('fee') || lowerLabel.includes('charge')) {
        return 'money';
      }
      if (lowerLabel.includes('date') || lowerLabel.includes('period')) {
        return 'date';
      }
      if (lowerLabel.includes('uom') || lowerLabel.includes('unit')) {
        return 'uom';
      }
      if (typeof value === 'number' || lowerLabel.includes('reading') || lowerLabel.includes('quantity') || lowerLabel.includes('multiplier')) {
        return 'number';
      }
      return 'text';
    };
    
    return (
      <div key={field.keyPath} className="kv-grid">
        <div className="kv-label flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{field.label}</span>
          {isCredit(field.value) && (
            <span className="credit-chip">Credit</span>
          )}
          {needsReview && (
            <Badge variant="destructive" className="text-xs">
              Review
            </Badge>
          )}
        </div>
        <div className={`kv-value ${isCredit(field.value) ? 'credit-amount' : ''}`}>
          <InlineFieldEditor
            value={isEdited ? editedFields[field.keyPath] : field.value}
            fieldType={getFieldType(field.label, field.value)}
            onSave={(newValue) => handleFieldEdit(field.keyPath, newValue)}
            onRevert={() => handleFieldRevert(field.keyPath)}
            isEdited={isEdited}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="ps-pane-body">
      {/* Header Card with Stats */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-3 py-3">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-900">Extracted Bill Data</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-gray-600">Confidence</span>
            <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
              {confidenceDisplay}%
            </Badge>
            <Separator orientation="vertical" className="h-4" />
            <small className="text-sm text-gray-600">Fields: {Object.values(fieldCounts).reduce((a, b) => a + b, 0)}</small>
            <small className="text-sm text-gray-600">Identifiers: {fieldCounts[CAT.Identifier]}</small>
            <small className="text-sm text-gray-600">Meters: {fieldCounts[CAT.Meter]}</small>
            <small className="text-sm text-gray-600">Line Items: {fieldCounts[CAT.LineItems]}</small>
            {(apiConnected || sheetsConnected) && (
              <>
                <Separator orientation="vertical" className="h-4" />
                <div className="flex items-center gap-2">
                  {apiConnected && (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <Link2 className="h-3 w-3" />
                      <span>API</span>
                    </div>
                  )}
                  {sheetsConnected && (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <Sheet className="h-3 w-3" />
                      <span>Sheets</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action Cards Row */}
      <div className="action-cards">
        <ExportCSVCard 
          onClick={handleExportCSV} 
          loading={loadingCSV}
          disabled={loadingApi || loadingSheets}
        />
        <APIConnectorModal onConnect={handleConnectAPI}>
          <ConnectAPICard 
            onClick={() => {}} 
            loading={loadingApi}
            disabled={loadingCSV || loadingSheets}
          />
        </APIConnectorModal>
        <GoogleSheetsConnectorModal onConnect={handleConnectSheets}>
          <ConnectGoogleSheetsCard 
            onClick={() => {}} 
            loading={loadingSheets}
            disabled={loadingCSV || loadingApi}
          />
        </GoogleSheetsConnectorModal>
      </div>

      {/* Tabs with Sticky Header */}
      <div className="ps-tabs">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="flex w-full h-10 overflow-x-auto gap-1 px-1">
            <TabsTrigger value="basic" className="gap-1.5 text-xs flex-shrink-0 px-3 py-2 bg-blue-50 hover:bg-blue-100 data-[state=active]:bg-blue-500 data-[state=active]:text-white">
              <FileText className="h-3 w-3 text-blue-600" />
              {CAT.Basic} ({count('Basic')})
            </TabsTrigger>
            <TabsTrigger value="financial" className="gap-1.5 text-xs flex-shrink-0 px-3 py-2 bg-green-50 hover:bg-green-100 data-[state=active]:bg-green-500 data-[state=active]:text-white">
              <Table2 className="h-3 w-3 text-green-600" />
              {CAT.Financial} ({count('Financial')})
            </TabsTrigger>
            <TabsTrigger value="address" className="gap-1.5 text-xs flex-shrink-0 px-3 py-2 bg-purple-50 hover:bg-purple-100 data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <MapPin className="h-3 w-3 text-purple-600" />
              {CAT.Address} ({count('Address')})
            </TabsTrigger>
            <TabsTrigger value="usage" className="gap-1.5 text-xs flex-shrink-0 px-3 py-2 bg-yellow-50 hover:bg-yellow-100 data-[state=active]:bg-yellow-500 data-[state=active]:text-white">
              <Zap className="h-3 w-3 text-yellow-600" />
              {CAT.Usage} ({count('Usage')})
            </TabsTrigger>
            <TabsTrigger value="meter" className="gap-1.5 text-xs flex-shrink-0 px-3 py-2 bg-orange-50 hover:bg-orange-100 data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Gauge className="h-3 w-3 text-orange-600" />
              {CAT.Meter} ({count('Meter')})
            </TabsTrigger>
            <TabsTrigger value="identifier" className="gap-1.5 text-xs flex-shrink-0 px-3 py-2 bg-pink-50 hover:bg-pink-100 data-[state=active]:bg-pink-500 data-[state=active]:text-white">
              <Hash className="h-3 w-3 text-pink-600" />
              {CAT.Identifier} ({count('Identifier')})
            </TabsTrigger>
            <TabsTrigger value="lineItems" className="gap-1.5 text-xs flex-shrink-0 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
              <List className="h-3 w-3 text-indigo-600" />
              {CAT.LineItems} ({count('LineItems')})
            </TabsTrigger>
            <TabsTrigger value="technical" className="gap-1.5 text-xs flex-shrink-0 px-3 py-2 bg-gray-50 hover:bg-gray-100 data-[state=active]:bg-gray-500 data-[state=active]:text-white">
              <Settings className="h-3 w-3 text-gray-600" />
              {CAT.Technical} ({count('Technical')})
            </TabsTrigger>
            <TabsTrigger value="other" className="gap-1.5 text-xs flex-shrink-0 px-3 py-2 bg-slate-50 hover:bg-slate-100 data-[state=active]:bg-slate-500 data-[state=active]:text-white">
              <MoreHorizontal className="h-3 w-3 text-slate-600" />
              {CAT.Other} ({count('Other')})
            </TabsTrigger>
          </TabsList>

          {/* Basic Fields Tab */}
          <TabsContent value="basic" className="mt-3 h-[calc(100vh-350px)] overflow-y-auto force-scrollbar">
            <div className="space-y-2 pr-2 overflow-x-auto">
              <div className="min-w-max">
              {canonicalResult.categories[CAT.Basic]?.map(field => renderField(field, 'basic'))}
              </div>
            </div>
          </TabsContent>

          {/* Financial Fields Tab */}
          <TabsContent value="financial" className="mt-3 h-[calc(100vh-350px)] overflow-y-auto force-scrollbar">
            <div className="space-y-2 pr-2 overflow-x-auto">
              <div className="min-w-max">
              {canonicalResult.categories[CAT.Financial]?.map(field => renderField(field, 'financial'))}
              </div>
            </div>
          </TabsContent>

          {/* Address Fields Tab */}
          <TabsContent value="address" className="mt-3 h-[calc(100vh-350px)] overflow-y-auto force-scrollbar">
            <div className="space-y-2 pr-2 overflow-x-auto">
              <div className="min-w-max">
              {canonicalResult.categories[CAT.Address]?.map(field => renderField(field, 'address'))}
              </div>
            </div>
          </TabsContent>

          {/* Usage Fields Tab */}
          <TabsContent value="usage" className="mt-3 h-[calc(100vh-350px)] overflow-y-auto force-scrollbar">
            <div className="space-y-4 pr-2 overflow-x-auto">
              <div className="min-w-max">
                {/* Usage Summary Table */}
                {canonicalResult.categories[CAT.Usage] && canonicalResult.categories[CAT.Usage].length > 0 ? (
                  <div className="bg-white rounded-lg border">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-yellow-600" />
                        <h3 className="text-sm font-medium text-gray-900">Usage Summary</h3>
                        <Badge variant="secondary" className="text-xs">
                          {canonicalResult.categories[CAT.Usage].length} {canonicalResult.categories[CAT.Usage].length === 1 ? 'field' : 'fields'}
                        </Badge>
                      </div>
                    </div>
                    <div className="overflow-auto max-h-[350px] force-scrollbar">
                      <div className="table-wrap">
                        <Table className="w-full">
                          <TableHeader className="sticky top-0 bg-gray-50 z-10">
                            <TableRow>
                              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                Label
                              </TableHead>
                              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                Value
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody className="divide-y divide-gray-200">
                            {canonicalResult.categories[CAT.Usage].map((field, index) => (
                              <TableRow key={index} className="hover:bg-gray-50">
                                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap font-medium">
                                  {field.label}
                                </TableCell>
                                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                  {String(field.value)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Zap className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No usage data available</p>
                    <p className="text-xs mt-2">Data may not be integrated for this supplier</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Meter Data Tab */}
          <TabsContent value="meter" className="mt-3 h-[calc(100vh-350px)] overflow-y-auto force-scrollbar">
            <div className="space-y-4 pr-2 overflow-x-auto">
              <div className="min-w-max">
                {canonicalResult.meters && canonicalResult.meters.length > 0 ? (
                <MeterTable 
                    data={canonicalResult.meters}
                  onEdit={handleFieldEdit}
                />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No meter data available
                </div>
              )}
              </div>
            </div>
          </TabsContent>

          {/* Identifier Data Tab */}
          <TabsContent value="identifier" className="mt-3 h-[calc(100vh-350px)] overflow-y-auto force-scrollbar">
            <div className="space-y-4 pr-2 overflow-x-auto">
              <div className="min-w-max">
                {canonicalResult.identifiers && canonicalResult.identifiers.length > 0 ? (
                  <IdentifierTable 
                    data={canonicalResult.identifiers}
                    onEdit={handleFieldEdit}
                  />
                ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No identifier data available
                </div>
              )}
              </div>
            </div>
          </TabsContent>

          {/* Line Items Tab */}
          <TabsContent value="lineItems" className="mt-3 h-[calc(100vh-350px)] overflow-y-auto force-scrollbar">
            <div className="space-y-4 pr-2 overflow-x-auto">
              <div className="min-w-max">
                {canonicalResult.lineItems && canonicalResult.lineItems.length > 0 ? (
                  <div className="bg-white rounded-lg border">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <List className="h-4 w-4 text-indigo-600" />
                        <h3 className="text-sm font-medium text-gray-900">Line Items</h3>
                        <Badge variant="secondary" className="text-xs">
                          {canonicalResult.lineItems.length} {canonicalResult.lineItems.length === 1 ? 'item' : 'items'}
                        </Badge>
                      </div>
                    </div>
                    <div className="overflow-auto max-h-[350px] force-scrollbar">
                      <div className="table-wrap">
                        <Table className="w-full min-w-[960px]">
                          <TableHeader className="sticky top-0 bg-gray-50 z-10">
                            <TableRow>
                              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                Identifier
                              </TableHead>
                              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                Meter #
                              </TableHead>
                              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                Description
                              </TableHead>
                              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                Qty
                              </TableHead>
                              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                Unit
                              </TableHead>
                              <TableHead className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                Rate
                              </TableHead>
                              <TableHead className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                Line Total
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody className="divide-y divide-gray-200">
                            {canonicalResult.lineItems.map((item, index) => (
                              <TableRow key={index} className="hover:bg-gray-50">
                                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                  {item.identifier}
                                </TableCell>
                                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                  {item.meterNumber}
                                </TableCell>
                                <TableCell className="px-4 py-2 text-sm text-gray-900">
                                  {item.description}
                                </TableCell>
                                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                  {item.quantity}
                                </TableCell>
                                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                  {item.unit}
                                </TableCell>
                                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap text-right">
                                  {item.rate}
                                </TableCell>
                                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap text-right font-medium">
                                  {item.total}
                                </TableCell>
                              </TableRow>
                            ))}
                            {/* Totals Footer */}
                            <TableRow className="bg-gray-50 font-semibold">
                              <TableCell colSpan={6} className="px-4 py-2 text-sm text-gray-900 text-right">
                                Total:
                              </TableCell>
                              <TableCell className="px-4 py-2 text-sm text-gray-900 text-right font-bold">
                                {canonicalResult.lineItems.reduce((sum, item) => {
                                  const total = typeof item.total === 'string' ? parseFloat(item.total) : item.total;
                                  return sum + (isNaN(total) ? 0 : total);
                                }, 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                    <List className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No line items available</p>
                    <p className="text-xs mt-2">Data may not be integrated for this supplier</p>
                </div>
              )}
              </div>
            </div>
          </TabsContent>

          {/* Technical Fields Tab */}
          <TabsContent value="technical" className="mt-3 h-[calc(100vh-350px)] overflow-y-auto force-scrollbar">
            <div className="space-y-2 pr-2 overflow-x-auto">
              <div className="min-w-max">
              {canonicalResult.categories[CAT.Technical]?.map(field => renderField(field, 'technical'))}
              </div>
            </div>
          </TabsContent>

          {/* Other Fields Tab */}
          <TabsContent value="other" className="mt-3 h-[calc(100vh-350px)] overflow-y-auto force-scrollbar">
            <div className="space-y-2 pr-2 overflow-x-auto">
              <div className="min-w-max">
              {canonicalResult.categories[CAT.Other]?.map(field => renderField(field, 'other'))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* User-Defined Fields Panel */}
      <div className="px-4 pb-4">
        <UserDefinedFieldsPanel
          fields={userDefinedFields}
          onChange={handleUDFChange}
          onSave={handleUDFSave}
          pdfSelectionProvider={getSelectedPdfText}
        />
      </div>
    </div>
  );
}
